import React, { forwardRef } from "react";
import { Slot } from "@radix-ui/react-slot";
import "./Button.css";

const variants = ["primary", "ghost", "soft", "outline", "danger", "subtle"];
const sizes = ["sm", "md", "lg", "icon"];

export const Button = forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    disabled = false,
    asChild = false,
    leftIcon = null,
    rightIcon = null,
    fullWidth = false,
    className = "",
    children,
    ...props
  },
  ref
) {
  const Comp = asChild ? Slot : "button";
  const classes = [
    "ui-btn",
    `ui-btn-${variants.includes(variant) ? variant : "primary"}`,
    `ui-btn-${sizes.includes(size) ? size : "md"}`,
    fullWidth ? "ui-btn-full" : "",
    loading ? "ui-btn-loading" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Slot (asChild) requires a single child element. Compose icons + label
  // inside the user's own element. For non-asChild, render the layout as
  // siblings under the <button>.
  if (asChild) {
    return (
      <Comp
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        data-loading={loading || undefined}
        {...props}
      >
        {React.cloneElement(React.Children.only(children), {
          children: (
            <>
              {loading && <span className="ui-btn-spinner" aria-hidden />}
              {!loading && leftIcon && (
                <span className="ui-btn-icon ui-btn-icon-left">{leftIcon}</span>
              )}
              <span className="ui-btn-label">{children.props.children}</span>
              {!loading && rightIcon && (
                <span className="ui-btn-icon ui-btn-icon-right">{rightIcon}</span>
              )}
            </>
          ),
        })}
      </Comp>
    );
  }

  return (
    <Comp
      ref={ref}
      className={classes}
      disabled={disabled || loading}
      data-loading={loading || undefined}
      {...props}
    >
      {loading && <span className="ui-btn-spinner" aria-hidden />}
      {!loading && leftIcon && <span className="ui-btn-icon ui-btn-icon-left">{leftIcon}</span>}
      <span className="ui-btn-label">{children}</span>
      {!loading && rightIcon && <span className="ui-btn-icon ui-btn-icon-right">{rightIcon}</span>}
    </Comp>
  );
});
